import type { AuthUser } from "../components/auth/AuthProvider";
import type { AdminPermission, AdminFeaturePermission } from "./acl/permissions";

/**
 * Any permission key the frontend can check against a user. Admin-scoped feature
 * permissions (e.g. `admin.system.deleteProtection`) live in a separate registry
 * from the core `AdminPermission`s but are resolved the same way here: an admin
 * holds them by default, the server's per-user deny override removes them, and
 * the route is the true gate. Widened so a feature-permission-gated surface can
 * share the same `hasPermission` check as every other admin page.
 */
export type CheckablePermission = AdminPermission | AdminFeaturePermission;

const normalize = (value: string | null | undefined) => value?.trim().toLowerCase() ?? "";

/**
 * Check if a user account is banned/suspended
 */
export const isBannedUser = (user: AuthUser | null | undefined): boolean => {
  if (!user) return false;
  return user.isBanned === true;
};

/**
 * Check if a user has admin-level access
 * This is a convenience helper; prefer checking specific permissions when available.
 */
export const isAdminUser = (user: AuthUser | null | undefined): boolean => {
  if (!user) return false;

  // userLevel >= 9 indicates admin access (legacy check)
  if (typeof user.userLevel === "number" && user.userLevel >= 9) return true;

  // Check role string
  const role = normalize(user.role);
  if (role === "admin" || role === "super admin" || role === "superadmin" || role === "administrator") {
    return true;
  }

  // Demo admin fallback
  return normalize(user.username) === "demo_admin";
};

/**
 * Check if a user is specifically a Super Admin
 */
export const isSuperAdminUser = (user: AuthUser | null | undefined): boolean => {
  if (!user) return false;

  // userLevel >= 10 indicates super admin
  if (typeof user.userLevel === "number" && user.userLevel >= 10) return true;

  const role = normalize(user.role);
  return role === "super admin" || role === "superadmin";
};

/**
 * Check if a user has staff-level access (includes admin)
 */
export const isStaffUser = (user: AuthUser | null | undefined): boolean => {
  if (!user) return false;

  // Admin includes staff
  if (isAdminUser(user)) return true;

  // userLevel >= 5 indicates staff
  if (typeof user.userLevel === "number" && user.userLevel >= 5) return true;

  const role = normalize(user.role);
  return role === "staff" || role === "moderator";
};

/**
 * Check if a user has a specific admin permission.
 * Super admins automatically have all permissions.
 */
export const hasPermission = (user: AuthUser | null | undefined, permission: CheckablePermission): boolean => {
  if (!user) return false;

  // Super admins have all permissions
  if (isSuperAdminUser(user)) return true;

  // Check explicit permissions. Compared by string identity because `permission`
  // may be a feature-permission key, which is not an element type of the
  // `AdminPermission[]` array (the server resolves both into the same list).
  if (user.permissions?.some((p) => p === permission)) return true;

  // Legacy fallback: Admins (userLevel >= 9) have all permissions
  // This ensures backward compatibility until roles are fully migrated
  if (isAdminUser(user)) return true;

  return false;
};

/**
 * Check if a user has any of the given permissions.
 */
export const hasAnyPermission = (user: AuthUser | null | undefined, permissions: CheckablePermission[]): boolean => {
  return permissions.some((p) => hasPermission(user, p));
};

/**
 * Check if a user has all of the given permissions.
 */
export const hasAllPermissions = (user: AuthUser | null | undefined, permissions: CheckablePermission[]): boolean => {
  return permissions.every((p) => hasPermission(user, p));
};
